
import React, { useState, useEffect } from "react";
import { CalendarViewProps } from "../types";
import { WeekHeader } from "./week-view/WeekHeader";
import { TimeGrid } from "./week-view/TimeGrid";
import { CalendarEvent } from "./week-view/CalendarEvent";
import { EventDialogs } from "./week-view/EventDialogs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useLanguage } from "@/components/LanguageContext";

export const WeekView = ({
  settings,
  weekDays = [],
  timeSlots = [],
  events = [],
  handleDateClick,
  handleEventClick,
  handleDeleteEvent,
  getEventColor,
  onEventUpdated
}: CalendarViewProps) => {
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localEvents, setLocalEvents] = useState<any[]>(events);
  const { t, language } = useLanguage();

  // Update local events whenever the events prop changes
  useEffect(() => {
    const filteredEvents = events.filter(e => !e._hideAfterApproval);
    setLocalEvents(filteredEvents);
  }, [events]);

  const gridTemplateColumns = `60px repeat(${weekDays.length}, 1fr)`;

  const getEventStatusColor = (event: any) => {
    if (event.status === 'pending') {
      return "bg-red-100 text-red-900 border border-red-200";
    }
    if (event.status === 'approved') {
      return "bg-green-100 text-green-900 border border-green-200";
    }
    return getEventColor(event.event_type);
  };

  const handleEventApprove = (event: any) => {
    setSelectedEvent(event);
    setShowApproveDialog(true);
  };

  const handleEventEdit = (event: any) => {
    setSelectedEvent(event);
    setShowEventDialog(true);
  };

  const handleEventDelete = async (event: any) => {
    if (isDeleting) return;
    
    try {
      setIsDeleting(true);
      
      if (event.event_type === 'class_request') {
        // Update the request status to rejected
        const { error: requestError } = await supabase
          .from('schedule_requests')
          .update({ status: 'rejected' })
          .eq('id', event.id);

        if (requestError) throw requestError;

        // Find and delete any related scheduled events
        // First check for direct relation via related_id
        const { data: relatedEvents, error: relatedError } = await supabase
          .from('schedule_events')
          .select('*')
          .eq('related_id', event.id);
          
        if (relatedError) console.error('Error finding related events:', relatedError);
        
        // Then also check by student and time
        const { data: timeRelatedEvents, error: timeError } = await supabase
          .from('schedule_events')
          .select('*')
          .eq('student_id', event.student_id)
          .eq('start_time', event.start_time);
          
        if (timeError) console.error('Error finding time-related events:', timeError);
        
        // Combine all related events
        const allRelatedEvents = [
          ...(relatedEvents || []),
          ...(timeRelatedEvents || [])
        ];
        
        if (allRelatedEvents.length > 0) {
          const { error: deleteError } = await supabase
            .from('schedule_events')
            .delete()
            .in('id', allRelatedEvents.map(e => e.id));

          if (deleteError) throw deleteError;
        }
        
        toast.success(language === 'en' ? 'Request rejected successfully' : 'Pedido rejeitado com sucesso');
      } else {
        // If deleting a scheduled event, check if there's an associated request
        const { data: relatedRequests } = await supabase
          .from('schedule_requests')
          .select('*')
          .eq('student_id', event.student_id)
          .eq('requested_date', event.start_time);
        
        // If there are related requests, update their status
        if (relatedRequests && relatedRequests.length > 0) {
          const { error: requestUpdateError } = await supabase
            .from('schedule_requests')
            .update({ status: 'rejected' })
            .in('id', relatedRequests.map(r => r.id));
            
          if (requestUpdateError) throw requestUpdateError;
        }
        
        // Also check for requests via related_id
        if (event.related_id) {
          const { error: relatedRequestError } = await supabase
            .from('schedule_requests')
            .update({ status: 'rejected' })
            .eq('id', event.related_id);
            
          if (relatedRequestError) console.error('Error updating related request:', relatedRequestError);
        }
        
        // Delete the actual event
        const { error } = await supabase
          .from('schedule_events')
          .delete()
          .eq('id', event.id);

        if (error) throw error;
        
        toast.success(language === 'en' ? 'Event deleted successfully' : 'Evento excluído com sucesso');
      }
      
      // Immediately remove the event from the local state
      removeEventLocally(event.id);
      
      // Also remove any related events with the same start time and student
      if (event.student_id && event.start_time) {
        setLocalEvents(prev => prev.filter(e => 
          !(e.student_id === event.student_id && 
            e.start_time === event.start_time && 
            e.id !== event.id)
        ));
      }
      
      if (onEventUpdated) onEventUpdated();
      
      setSelectedEvent(null);
      setShowEventDialog(false);
      
    } catch (error) {
      console.error('Erro ao deletar evento:', error);
      toast.error(language === 'en' ? 'An error occurred when deleting the event' : 'Ocorreu um erro ao excluir o evento');
    } finally {
      setIsDeleting(false);
    }
  };

  const removeEventLocally = (eventId: string) => {
    setLocalEvents(prev => prev.filter(e => e.id !== eventId));
  };
  
  const handleEventUpdate = () => {
    // When an event is updated (especially approval), immediately update the UI
    if (selectedEvent && selectedEvent.event_type === 'class_request') {
      // Update the status of the request to 'approved' in our local state
      setLocalEvents(prev => prev.map(e => {
        if (e.id === selectedEvent.id) {
          return { ...e, status: 'approved' };
        }
        return e;
      }));
    }
    
    // Also trigger a refresh from the server
    if (onEventUpdated) onEventUpdated();
  };

  if (!settings || !weekDays.length) return null;

  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[800px] relative">
        <div className="grid relative" style={{ gridTemplateColumns }}>
          {/* Calendar Header */}
          <WeekHeader weekDays={weekDays} />

          {/* Time Grid */}
          <TimeGrid 
            timeSlots={timeSlots} 
            weekDays={weekDays} 
            handleDateClick={handleDateClick}
            settings={settings}
          />

          {/* Calendar Events */}
          {localEvents.map(event => (
            <CalendarEvent
              key={event.id}
              event={event}
              weekDays={weekDays}
              settings={settings}
              getEventStatusColor={getEventStatusColor}
              onEventClick={handleEventClick}
              onEventApprove={handleEventApprove}
              onEventEdit={handleEventEdit}
              onEventDelete={handleEventEdit} // We use edit here, as the dialog has the delete option
            />
          ))}
        </div>
      </div>

      {/* Dialogs */}
      <EventDialogs
        showApproveDialog={showApproveDialog}
        showEventDialog={showEventDialog}
        selectedEvent={selectedEvent}
        onCloseApprove={() => {
          setShowApproveDialog(false);
          setSelectedEvent(null);
        }}
        onCloseEvent={() => {
          setShowEventDialog(false);
          setSelectedEvent(null);
        }}
        onEventUpdated={handleEventUpdate}
        onEventDeleted={handleEventDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};
